import { NotificationService } from '@optsolservices/ngx-notification';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfirmDialogTemplateComponent } from '@optsolservices/ngx-confirm-dialog';
import { BaseService } from 'src/app/core/services/base.service';
import { ConfirmWithFreeTextReasonDialogComponent } from 'src/app/shared/confirm-with-free-text-reason-dialog/confirm-with-free-text-reason-dialog.component';
import { OiGroupDialogData, OiGroupProcessDialogComponent } from 'src/app/shared/oi-group-process-dialog/oi-group-process-dialog.component';

@Component({
  selector: 'app-action-eft-request-dialog',
  templateUrl: './action-eft-request-dialog.component.html',
  styleUrls: ['./action-eft-request-dialog.component.scss']
})
export class ActionEftRequestDialogComponent implements OnInit {
  blockUI: boolean = false;
  requestId: number;
  request = null;
  stores: any[] = [];
  form: FormGroup;

  storeId: number;
  containerId: number;
  equipmentTypeCode: string;
  formValid: boolean = false;
  formValue: any;
  refundReasonId: number;

  constructor(
    public dialogRef: MatDialogRef<ActionEftRequestDialogComponent>,
    private baseService: BaseService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {
    this.requestId = data.additionalData.data.requestId
    this.form = fb.group({
      requestId: [null, Validators.compose([Validators.required])],
      storeId: [null, Validators.compose([Validators.required])],
      amount: [null],
      bank: [null, Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(50)])],
      branchCode: [null, Validators.compose([Validators.minLength(1), Validators.maxLength(10)])],
      accountNumber: [null, Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(20)])],
      comment: [null, Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(200)])],
      actionComment: [null, Validators.compose([Validators.required, Validators.minLength(1), Validators.maxLength(200)])],
    });
    this.form.disable();
  }

  ngOnInit() {
    this.baseService.executeCommand<any>('portal_finance_eft_refund_request_dialog_config', { requestId: this.requestId })
      .subscribe(response => {
        if (response.responseCode === 0) {
          this.request = response.data.request;
          this.form.patchValue(this.request);
          this.stores = response.data.stores;
          if (this.stores && this.stores.length === 1) {
            this.form.controls['storeId'].setValue(this.stores[0].storeId);
          }
        } else {
          this.dialogRef.close(undefined);
        }
      });
  }

  refundReasonChanged(event: {
    refundReasonId: number;
  }) {
    this.refundReasonId = event.refundReasonId;
  }

  decline() {
    const confirmDialogRef = this.dialog.open(ConfirmDialogTemplateComponent, {
      width: '450px',
      data: {
        title: 'Declining EFT Refund Request',
        text: `IMPORTANT: Are you sure you want to decline this refund request? This cannot be undone.`,
        confirmButtonText: 'Decline Refund',
        cancelButtonText: 'Cancel',
        showConfirmButton: true,
        showCancelButton: true
      }
    });

    confirmDialogRef.afterClosed().subscribe(confirmResponse => {
      if (confirmResponse.confirmButtonClicked === true) {
        this.declineRefund();
      }
    })

  }

  declineRefund() {
    const dialogRef = this.dialog.open(ConfirmWithFreeTextReasonDialogComponent, {
      width: '450px',
      data: {
        title: 'Reason for Declining',
        text: 'Please provide a reason for declining the request. This will be visible to the salesperson.',
        confirmButtonText: 'Decline',
        cancelButtonText: 'No',
        showConfirmButton: true,
        showCancelButton: true,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.confirmButtonClicked === true) {
        this.blockUI = true;
        this.baseService.executeCommand<any>('portal_finance_eft_refund_request_decline', { requestId: this.requestId, comment: result.reasonText })
          .subscribe(
            response => {
              if (response.responseCode === 0) {
                this.dialogRef.close(true);
              }
            },
            error => {
              this.blockUI = false;
            });
      }
    });
  }

  paymentChanged(event: {
    storeId: number;
    containerId: number;
    equipmentTypeCode: string;
    formValid: boolean;
    formValue: any;
  }) {
    this.storeId = event.storeId;
    this.containerId = event.containerId;
    this.equipmentTypeCode = event.equipmentTypeCode;
    this.formValid = event.formValid;
    this.formValue = event.formValue;
  }

  processed() {
    if (this.formValue.amountTendered < this.request.invoiceBalance) {
      const dialogRef = this.dialog.open(ConfirmDialogTemplateComponent, {
        width: '450px',
        data: {
          title: 'Overpaying an invoice',
          text: `Refunding more (${(this.formValue.amountTendered).toFixed(2)}) than what's due on the invoice (${(this.request.invoiceBalance).toFixed(2)}) is not allowed.`,
          cancelButtonText: 'Ok',
          showConfirmButton: false,
          showCancelButton: true
        }
      });

    } else {
      const confirmDialogRef = this.dialog.open(ConfirmDialogTemplateComponent, {
        width: '450px',
        data: {
          title: 'Processing EFT Refund Request',
          text: `IMPORTANT: I confirm that I have processed the actual EFT Refund to the customer.`,
          confirmButtonText: 'Process Refund',
          cancelButtonText: 'Cancel',
          showConfirmButton: true,
          showCancelButton: true
        }
      });

      confirmDialogRef.afterClosed().subscribe(confirmResponse => {
        if (confirmResponse.confirmButtonClicked === true) {
          switch (this.request.paymentRequestType) {
            case "DEPOSIT-REFUND":
              this.processDepositRefund();
              break;
            case "REFUND":
              this.processInvoiceRefund();
              break;
            default:
              this.notificationService.error(`Payment Request Type '${this.request.paymentRequestType}' is not supported.`)

          }
        }
      })

    }
  }

  processInvoiceRefund() {
    const payload = {
      requestId: this.requestId,
      storeId: this.storeId,
      containerId: this.containerId,
      equipmentTypeCode: this.equipmentTypeCode,
      amountPaid: this.formValue.amountTendered,
      paymentData: this.formValue,
      refundReasonId: this.refundReasonId
    };

    const dialogData: OiGroupDialogData = {
      title: 'Processing Refund',
      function: 'portal_oi_group_finance_eft_refund_request_refund_invoice',
      postBody: payload
    };

    const ref = this.dialog.open(OiGroupProcessDialogComponent, {
      data: dialogData,
      width: '450px'
    });

    ref.afterClosed().subscribe(oiResult => {
      if (oiResult.responseCode === 0 && oiResult.data && oiResult.data.groupProcessedSuccessfully === true) {
        this.dialogRef.close(true);
      }
    });
  }

  processDepositRefund() {
    this.blockUI = true;
    this.baseService.executeCommand<any>('portal_finance_eft_refund_request_refund_deposit', {
      requestId: this.requestId,
      storeId: this.storeId,
      containerId: this.containerId,
      amountPaid: this.formValue.amountTendered,
      paymentData: this.formValue,
      refundReasonId: this.refundReasonId
    }).subscribe(
      response => {
        if (response.responseCode === 0) {
          this.dialogRef.close(true);

        } else {
          this.blockUI = false;
        }
      },
      error => {
        this.blockUI = false;
      });
  }
}
